import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useAuth } from "../../../app/authHooks";
import { useNavigate, Link } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { Eye, EyeClosed, UserScan } from "iconoir-react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../app/store";
import { clearForgotUsername } from "../../../app/authSlice";

const initialValues = {
  confirmationCode: "",
  password: "",
  confirmPassword: "",
};

const resetSchema = yup.object().shape({
  confirmationCode: yup.string().required("Verification code is required."),
  password: yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Must contain a lowercase letter")
    .matches(/[A-Z]/, "Must contain an uppercase letter")
    .matches(/[0-9]/, "Must contain a number")
    .matches(/[^a-zA-Z0-9]/, "Must contain a special character")
    .required("Password is required."),
  confirmPassword: yup.string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required."),
});

const ResetPasswordForm = () => {
  const theme = useTheme();
  const colors = theme.palette;
  const auth = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const forgotUsername = useSelector((state: RootState) => state.auth.forgotUsername);

  const handleTogglePasswordVisibility = (field: "password" | "confirmPassword") => {
    if (field === "password") {
      setShowPassword((prev) => !prev);
    } else {
      setShowConfirmPassword((prev) => !prev);
    }
  };

  const handleFormSubmit = async (values: any) => {
    setIsLoading(true);
    if (!forgotUsername) {
      setIsLoading(false);
      enqueueSnackbar("Username not found. Please restart the process.", { variant: "error" });
      navigate("/auth/forgot-password");
      return;
    }
    const result = await auth.confirmNewPassword(forgotUsername, values.confirmationCode, values.password);
    setIsLoading(false);
    if (result.success) {
      enqueueSnackbar(result.message, { variant: "success" });
      dispatch(clearForgotUsername());
      navigate("/auth/login");
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  return (
    <Box sx={{ background: colors.primary.light, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      <Box sx={{ marginBottom: 4 }}>
        <Box
          component="img"
          alt="ss&c-logo"
          src="../../../assets/images/ssc-hubwise-logo.svg"
          sx={{
            width: 250,
            height: "auto"
          }}
        />
      </Box>
      <Box
        sx={{
          p: 5,
          background: colors.grey[900],
          borderRadius: 4,
          boxShadow: "1px 3px 10px rgba(202, 202, 202, 0.83)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[400] }}>Admin Platform</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Reset Password</Typography>
        </Box>

        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={resetSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 5
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    label="Verification Code"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.confirmationCode}
                    name="confirmationCode"
                    error={!!touched.confirmationCode && !!errors.confirmationCode}
                    helperText={!!touched.confirmationCode && errors.confirmationCode}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    label="New Password"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    name="password"
                    error={!!touched.password && !!errors.password}
                    helperText={!!touched.password && errors.password}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ fontSize: 14, ":hover": { cursor: "pointer" } }}>
                          <IconButton
                            sx={{ p: 0, border: 0, boxShadow: 0, borderRadius: 0, ":hover": { backgroundColor: colors.grey[900], boxShadow: 0 } }}
                            onClick={() => handleTogglePasswordVisibility("password")}
                          >
                            {showPassword ? <Eye /> : <EyeClosed />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm New Password"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.confirmPassword}
                    name="confirmPassword"
                    error={!!touched.confirmPassword && !!errors.confirmPassword}
                    helperText={!!touched.confirmPassword && errors.confirmPassword}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ fontSize: 14, ":hover": { cursor: "pointer" } }}>
                          <IconButton
                            sx={{ p: 0, border: 0, boxShadow: 0, borderRadius: 0, ":hover": { backgroundColor: colors.grey[900], boxShadow: 0 } }}
                            onClick={() => handleTogglePasswordVisibility("confirmPassword")}
                          >
                            {showConfirmPassword ? <Eye /> : <EyeClosed />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="end" mt="20px">
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    endIcon={isLoading ? <CircularProgress size={20} /> : null}
                  >
                    {isLoading ? "Resetting..." : "Reset Password"}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", marginTop: 2, gap: 1 }}>
        <Typography variant="h5">By clicking continue, you agree to our</Typography>
        <Typography variant="h5"><Typography variant="h5" component={Link} to="#" sx={{
          color: colors.grey[400],
          ":hover": {
            color: colors.primary.main
          }
        }}>
          Terms of Service
        </Typography> and <Typography variant="h5" component={Link} to="#" sx={{
          color: colors.grey[400],
          ":hover": {
            color: colors.primary.main
          }
        }}>
            Privacy Policy
        </Typography></Typography>
      </Box>
    </Box>
  );
};

export default ResetPasswordForm;
